/**
 * Figure S7's "💰 Duplicate funding asks" card — funding programmes named by
 * more than one of this portfolio's entities, and how many of those are
 * already linked to an overlap group.
 *
 * COUNTS ONLY. The funding rollup also carries dollar amounts; none of them
 * is shown here, because the question this card answers is "are two of my
 * entities asking the same body for the same work?", not "how much".
 */
import type { OverlapFundingRollup } from '../../types/whitespace'

export interface DuplicateFundingAsksProps {
  rollup?: OverlapFundingRollup
  /**
   * Opens the overlap group behind these asks. Called with the group's id when
   * a programme really points at one, and with null when none does — the card
   * still needs a way out, and the Overlap view itself is the honest
   * destination. Round 2 shipped with this prop unwired, so this was the only
   * card on S7 with no link at all.
   */
  onOpenGroup?: (groupId: string | null) => void
}

export interface FundingCounts {
  multiProjectProgrammes: number
  linkedToAGroup: number
  /** The first programme row that really points at a group, for the link. */
  firstLinkedGroupId: string | null
}

export function countFundingAsks(rollup: OverlapFundingRollup | undefined): FundingCounts {
  const rows = rollup?.funding_mentions_rows ?? []
  const linked = rows.filter((row) => row.state === 'linked' && !!row.group_id)
  return {
    multiProjectProgrammes: rollup?.multi_project_programme_count ?? 0,
    linkedToAGroup: new Set(linked.map((row) => row.group_id as string)).size,
    firstLinkedGroupId: (linked[0]?.group_id as string) ?? null,
  }
}

function Row({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-iflow-900/5 py-2 first:border-t-0">
      <span className="text-[12.5px] text-gray-500">{label}</span>
      <span className="text-[12.5px] font-semibold text-iflow-900">{value}</span>
    </div>
  )
}

export default function DuplicateFundingAsks({ rollup, onOpenGroup }: DuplicateFundingAsksProps) {
  const counts = countFundingAsks(rollup)

  return (
    <div data-part="DuplicateFundingAsks" className="light-card p-4">
      <h3 className="text-[15px] font-bold text-iflow-900">💰 Duplicate funding asks</h3>
      <p className="mt-1 text-[12px] text-gray-500">Programmes named by more than one entity.</p>

      {counts.multiProjectProgrammes === 0 ? (
        <p className="mt-2 text-[12.5px] text-gray-600">
          No funding programme has been named by more than one of your entities.
        </p>
      ) : (
        <>
          <div className="mt-2">
            <Row label="Programmes named by ≥ 2 entities" value={counts.multiProjectProgrammes} />
            <Row label="Linked to an overlap group" value={counts.linkedToAGroup} />
          </div>
          {onOpenGroup && (
            <div className="mt-2 text-right">
              <button
                type="button"
                onClick={() => onOpenGroup(counts.firstLinkedGroupId)}
                title={
                  counts.firstLinkedGroupId
                    ? 'Opens the overlap group these programmes are linked to.'
                    : 'None of these programmes is linked to an overlap group yet, so this opens the Overlap view.'
                }
                className="text-[12px] font-semibold text-iflow-700 hover:text-iflow-800 hover:underline"
              >
                open the group →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
